/**
 * @fileoverview API utility functions for HTTP requests and authentication
 * Provides standardized methods for API communication with encryption/decryption
 * Handles authentication headers, user context, and response processing
 * 
 * @version 1.0.0
 * @author Absolute Jobs Team
 * @security Implements encrypted communication and secure token management
 */

import EncryptDecryptService from "./EncryptDecryptService";
import { refreshOauthTokenIfNeeded } from "./OAuthService";
import type { RootState } from "store";
import store from "store";

/**
 * HTTP headers interface for API requests
 * Standardizes header format across all API calls
 */
type Headers = {
  /** Content type acceptance header */
  Accept?: string;
  /** Request content type */
  "Content-Type"?: string;
  /** Bearer token authorization */
  Authorization?: string;
  /** Authentication token (legacy) */
  AuthToken?: string;
  /** API key for service access */
  "x-api-key"?: string;
  /** Tenant identifier for multi-tenant architecture */
  "x-tenant-id"?: string;
};

/**
 * Toggle the global loading overlay
 * Controls the visibility of the application-wide loading indicator
 * 
 * @param show - Whether to show (true) or hide (false) the overlay
 * 
 * @example
 * ```typescript
 * toggleOverlay(true);  // Show loading overlay
 * // ... perform API request
 * toggleOverlay(false); // Hide loading overlay
 * ```
 * 
 * @security No security implications - purely UI functionality
 */
export const toggleOverlay = (show: boolean) => {
  const overlay = document.getElementById("overlay") as HTMLDivElement | null;
  if (overlay) {overlay.style.display = show ? "flex" : "none";}
};

/**
 * Decrypts and parses API responses
 * Handles both blob (file) responses and encrypted JSON responses
 * 
 * @param data - The raw response data from API
 * @param isBlob - Whether the response is a blob (file) or encrypted JSON
 * @returns Parsed JSON object for non-blob responses, or blob URL for file responses
 * 
 * @security Decrypts sensitive data using EncryptDecryptService
 * 
 * @example
 * ```typescript
 * // For JSON responses
 * const jsonData = await decryptResponse(encryptedResponse, false);
 * 
 * // For file downloads
 * const fileUrl = await decryptResponse(blobResponse, true);
 * ```
 */
export const decryptResponse = async (data: any, isBlob: boolean) => {
  if (isBlob) {return URL.createObjectURL(data);}
  // Response is plaintext JSON (TO-38233; envelope removed).
  return data;
};

/**
 * Get current user's ID and role from Redux state
 * Provides access to authenticated user context for API requests
 * 
 * @returns Object containing current user's ID and role
 * 
 * @security Accesses authenticated user data from secure Redux store
 * 
 * @example
 * ```typescript
 * const { userId, role } = getCurrentUserId();
 * console.log(`User ${userId} has role ${role}`);
 * ```
 */
export const getCurrentUserId = () => {
  const state: RootState = store.getState();
  return {
    userId: state.auth.userId,
    role: state.auth.role,
  };
};

/**
 * Extract and decrypt authentication token from persisted state
 * Retrieves user authentication token from encrypted localStorage
 * 
 * @returns Decrypted authentication token string
 * 
 * @security 
 * - Accesses encrypted data from localStorage
 * - Uses EncryptDecryptService for secure token extraction
 * - Handles nested JSON parsing safely
 * 
 * @example
 * ```typescript
 * const token = getAuthToken();
 * // Use token in Authorization header
 * ```
 * 
 * @throws May throw if localStorage is corrupted or encryption fails
 */
export const getAuthToken = (): string => {
  // Read from the live redux store (same source as getCurrentUserId) rather than the
  // persisted localStorage copy: persist:root lags the store, so immediately after login
  // the persisted token wasn't written yet — the first authenticated calls (getUserPermissions,
  // getAppLang) then sent a stale/empty bearer and Sanctum 401'd (TO-38233). The store holds
  // the token right after login's dispatch and after rehydrate.
  const state: RootState = store.getState();
  const token = state.auth.token;
  if (token) {
    return token;
  }
  // Fallback for a hard load before rehydration completes: the persisted copy.
  // TO-38127 (f_fesec_d4e29ffeed): the persisted store is now plaintext; keep a
  // fallback that still reads a legacy encrypted persist:root from an older build.
  const persisted = JSON.parse(
    JSON.parse(localStorage.getItem("persist:root") ?? "{}")?.auth ?? "{}"
  );
  if (persisted && typeof persisted === "object") {
    return persisted?.token ?? ""; // plaintext auth slice
  }
  try {
    return JSON.parse(EncryptDecryptService.decryptData(persisted))?.token ?? ""; // legacy ciphertext
  } catch {
    return "";
  }
};

/**
 * Build standard API headers with authentication and content type
 * Constructs complete header object for API requests with proper authentication
 * 
 * @param isJson - Whether the request content is JSON (default: true)
 * @param isFormData - Whether the request content is form data (default: false)
 * @returns Promise resolving to complete headers object
 * 
 * @security 
 * - Includes Bearer token authentication
 * - Adds OAuth API key for service authorization
 * - Includes tenant ID for multi-tenant security
 * 
 * @example
 * ```typescript
 * // For JSON requests
 * const headers = await getHeaders();
 * 
 * // For file uploads
 * const formHeaders = await getHeaders(false, true);
 * 
 * // For plain text requests
 * const textHeaders = await getHeaders(false, false);
 * ```
 */
export const getHeaders = async (
  isJson = true,
  isFormData = false
): Promise<Headers> => {
  const token = getAuthToken();
  const oAuthToken = await refreshOauthTokenIfNeeded();

  return {
    Accept: "application/json",
    "Content-Type": isJson
      ? "application/json"
      : isFormData
      ? "multipart/form-data"
      : undefined,
    Authorization: `Bearer ${token}`,
    "x-api-key": oAuthToken,
    "x-tenant-id": process.env.REACT_APP_CLIENT,
  };
};

/**
 * Logout user and redirect to login page
 * Clears all localStorage data and navigates to login
 * 
 * @security 
 * - Completely clears localStorage to remove all tokens and user data
 * - Forces navigation to login page
 * - Prevents unauthorized access after logout
 * 
 * @example
 * ```typescript
 * // Call on logout button click or session expiry
 * logoutUser();
 * ```
 * 
 * @note Uses window.location.href for hard navigation to ensure complete logout
 */
export const logoutUser = () => {
  localStorage.clear();
  window.location.href = "/login";
};

/**
 * Fetch a protected candidate-backend resource with the user's bearer token and
 * return an object URL, for use as an <iframe src> or a download link.
 *
 * These routes (consent preview, PDF/consent downloads) moved behind auth:sanctum
 * (TO-38233); a plain <a href>/<iframe src> cannot send an Authorization header,
 * so the resource must be fetched with the bearer and handed to the browser as a
 * blob object URL instead. Returns null on failure; a 401 triggers logout.
 *
 * @param path - Path on the backend, starting with `/api/...`
 * @param baseUrl - Backend base URL; defaults to the candidate backend.
 */
export const fetchAuthedBlobUrl = async (
  path: string,
  baseUrl: string = process.env.REACT_APP_BACKEND_SERVICE ?? ""
): Promise<string | null> => {
  try {
    const token = getAuthToken();
    const base = baseUrl.replace(/\/+$/, "");
    const response = await fetch(`${base}${path}`, {
      // Accept JSON so an unauthenticated response renders as a clean 401 (handled
      // below) rather than an HTML login redirect; the file/HTML success response
      // sets its own Content-Type and is unaffected.
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });
    if (response.status === 401) {
      logoutUser();
      return null;
    }
    if (!response.ok) {
      return null;
    }
    return URL.createObjectURL(await response.blob());
  } catch (error) {
    console.error("fetchAuthedBlobUrl error:", error);
    return null;
  }
};

/**
 * Download a protected candidate-backend file with the user's bearer token.
 * Replaces plain <a href> downloads for routes now behind auth:sanctum (TO-38233).
 *
 * @param path - Path on the backend, starting with `/api/...`
 * @param fileName - Suggested filename for the saved file
 * @param baseUrl - Backend base URL; defaults to the candidate backend.
 */
export const downloadAuthedFile = async (
  path: string,
  fileName: string,
  baseUrl?: string
): Promise<void> => {
  const url = await fetchAuthedBlobUrl(path, baseUrl);
  if (!url) {
    return;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// TODO: Consider adding the following improvements:
// - Clear specific keys instead of entire localStorage
// - Add logout confirmation dialog
// - Implement server-side token invalidation
// - Add proper error handling for navigation
// - Consider using React Router for navigation instead of window.location
